import React, { useRef, useState, useCallback } from 'react'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif']
const MAX_SIZE = 10 * 1024 * 1024 // 10MB

function ImageUpload({ images, onImagesChange, maxImages = 10 }) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  const validateFile = (file) => {
    // Some browsers report empty type for HEIC — fall back to extension check
    const isHeicByExt = /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name)
    if (!ALLOWED_TYPES.includes(file.type) && !isHeicByExt) {
      return 'Invalid file type. Please upload JPEG, PNG, GIF, WebP, or HEIC images.'
    }
    if (file.size > MAX_SIZE) {
      return 'File too large. Maximum size is 10MB.'
    }
    return null
  }

  const handleFiles = useCallback((files) => {
    setError(null)
    const newImages = []

    for (const file of files) {
      if (images.length + newImages.length >= maxImages) {
        setError(`Maximum ${maxImages} images allowed.`)
        break
      }

      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        continue
      }

      // Add preview URL
      file.preview = URL.createObjectURL(file)
      newImages.push(file)
    }

    if (newImages.length > 0) {
      onImagesChange([...images, ...newImages])
    }
  }, [images, maxImages, onImagesChange])

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(Array.from(e.dataTransfer.files))
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleInputChange = (e) => {
    handleFiles(Array.from(e.target.files))
    e.target.value = '' // Reset input
  }

  const removeImage = (index) => {
    const newImages = [...images]
    URL.revokeObjectURL(newImages[index].preview)
    newImages.splice(index, 1)
    onImagesChange(newImages)
  }

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`drop-zone cursor-pointer ${isDragging ? 'active' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          onChange={handleInputChange}
          className="hidden"
        />

        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-gray-600">
            <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500">
            JPEG, PNG, GIF, WebP, HEIC up to 10MB ({images.length}/{maxImages})
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={image.preview}
                alt={`Upload ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <p className="mt-1 text-xs text-gray-500 truncate">{image.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUpload
